// src/auditoria/components/tx-alert-dialog/tx-alert-provider.tsx
import React, { useRef, useState, useEffect, useContext, useCallback, createContext } from 'react';

import {
  Box,
  Alert,
  Dialog,
  Button,
  Snackbar,
  TextField,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';

/**
 * Variante do diálogo. `destructive` (opt-in) rende o verbo da ação em error e
 * é para os casos P4 do ADR-MODAL-DRAWER-PAGE (Arquivar, Cancelar CDA etc.).
 * O campo de motivo CONTINUA obrigatório na variante nova sempre que
 * `motivo_operacao` é passado — ação irreversível auditável exige motivo
 * textual (ADR P4). A variante muda só o visual do Confirmar, nunca a regra.
 */
export type TxAlertVariant = 'default' | 'destructive';

export interface TxAlertOptions {
  variant?: TxAlertVariant;
  /** Rótulo do botão de confirmação — o verbo da ação (ex.: 'Arquivar'). */
  confirmLabel?: string;
}

// 1) Ponteiro global (vai receber a impl real em runtime)
let _openAlertGlobal: (
  title: string,
  message: string,
  motivo_operacao?: string,
  options?: TxAlertOptions
) => Promise<boolean | { confirmed: boolean; motivo: string }> = () =>
  Promise.reject(new Error('AlertProvider não montado'));

// 2) Função que você exporta e importa direto
export function abrirAlertGlobal(
  title: string,
  message: string,
  motivo_operacao?: string,
  options?: TxAlertOptions
): Promise<boolean | { confirmed: boolean; motivo: string }> {
  return _openAlertGlobal(title, message, motivo_operacao, options);
}

// 3) Contexto / Provider
type AlertContextType = {
  open: (
    title: string,
    message: string,
    motivo_operacao?: string,
    options?: TxAlertOptions
  ) => Promise<boolean | { confirmed: boolean; motivo: string }>;
};

const AlertContext = createContext<AlertContextType | null>(null);

export const AlertProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [opts, setOpts] = useState<{
    title: string;
    message: string;
    motivo_operacao?: string;
    variant?: TxAlertVariant;
    confirmLabel?: string;
  } | null>(null);
  const [open, setOpen] = useState(false);
  const [motivoText, setMotivoText] = useState('');
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const resolverRef = useRef<
    ((result: boolean | { confirmed: boolean; motivo: string }) => void) | undefined
  >(undefined);
  const resultRef = useRef<boolean | { confirmed: boolean; motivo: string }>(false);

  // impl real
  const openAlert = useCallback(
    (title: string, message: string, motivo_operacao?: string, options?: TxAlertOptions) => {
      setOpts({ title, message, motivo_operacao, ...options });
      setMotivoText('');
      setOpen(true);
      return new Promise<boolean | { confirmed: boolean; motivo: string }>((resolve) => {
        resolverRef.current = resolve;
      });
    },
    []
  );

  // inicializa o ponteiro global
  useEffect(() => {
    _openAlertGlobal = openAlert;
    return () => {
      _openAlertGlobal = () => Promise.reject(new Error('AlertProvider desmontado'));
    };
  }, [openAlert]);

  const handleClose = (confirmed: boolean) => {
    if (opts?.motivo_operacao && confirmed) {
      if (!motivoText.trim()) {
        setErrorMessage(`O campo "${opts.motivo_operacao}" é obrigatório`);
        setShowErrorAlert(true);
        return;
      }
      resultRef.current = { confirmed: true, motivo: motivoText };
    } else {
      resultRef.current = confirmed;
    }
    setOpen(false);
  };
  const handleExited = () => {
    resolverRef.current?.(resultRef.current);
    setOpts(null);
    setMotivoText('');
  };

  return (
    <AlertContext.Provider value={{ open: openAlert }}>
      {children}
      <Dialog
        open={open}
        onClose={() => handleClose(false)}
        TransitionProps={{ onExited: handleExited }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{opts?.title}</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: opts?.motivo_operacao ? 2 : 0 }}>{opts?.message}</Box>
          {opts?.motivo_operacao && (
            <TextField
              fullWidth
              label={opts.motivo_operacao}
              value={motivoText}
              onChange={(e) => setMotivoText(e.target.value)}
              multiline
              rows={3}
              variant="outlined"
              size="small"
            />
          )}
        </DialogContent>
        {/* Ações à direita (default do DialogActions): Cancelar + verbo da ação. */}
        <DialogActions>
          <Button onClick={() => handleClose(false)}>Cancelar</Button>
          <Button
            variant="outlined"
            color={opts?.variant === 'destructive' ? 'error' : 'success'}
            onClick={() => handleClose(true)}
            autoFocus
          >
            {opts?.confirmLabel ?? 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={showErrorAlert}
        autoHideDuration={4000}
        onClose={() => setShowErrorAlert(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowErrorAlert(false)} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </AlertContext.Provider>
  );
};

// hook opcional, se precisar
export function useAlert() {
  const ctx = useContext(AlertContext);
  if (!ctx) throw new Error('useAlert só funciona dentro de AlertProvider');
  return ctx.open;
}
